import * as Api from '@ViewModels';
import { History } from 'history';
import React from 'react';
import { UseFormClearErrors, UseFormGetValues, UseFormReset } from 'react-hook-form';
import { toast } from 'sonner';
import { ErrorMessageViewModel } from '../../../../../extViewmodels/ViewModels';
import {
	invalidateInfiniteSurveys,
	invalidateSurveyById,
	useAccountStripeApiPublicApiKeys,
	useEventRegistrationMutation,
	useImageAttachmentMutation,
	useReorderEventRegistrationSurveyFieldsMutation,
	useUpdateEventSurveyMutation,
} from '../../../../../queries';
import { EventRegistrationFormData, TabValues } from '../utils';

interface UseEventRegistrationMutationsProps {
	impersonationContext?: Api.IImpersonationContext;
	history: History;
	errorMessages: ErrorMessageViewModel;
	logApiError: (message: string, error: Api.IOperationResultNoValue) => void;
	getValues: UseFormGetValues<EventRegistrationFormData>;
	reset: UseFormReset<EventRegistrationFormData>;
	clearErrors: UseFormClearErrors<EventRegistrationFormData>;
	setState: React.Dispatch<
		React.SetStateAction<{
			enableRequestAttendeesToRSVP: boolean;
			fileInputModalOpen: boolean;
			now: Date;
			selectedImage: Api.IFileAttachment | null;
			customFormIsDisabled: boolean;
			selectedTab: TabValues;
			showEditSurveyName: boolean;
			showReorderModal: boolean;
		}>
	>;
	onReorderSuccess?: (fields: Api.IFormField<string>[]) => void;
}

// Hook for mutations and queries
export const useEventRegistrationSurveyMutations = ({
	impersonationContext,
	history,
	errorMessages,
	logApiError,
	getValues,
	reset,
	clearErrors,
	setState,
	onReorderSuccess,
}: UseEventRegistrationMutationsProps) => {
	// Query for Stripe API keys
	const stripePublicKeys = useAccountStripeApiPublicApiKeys({
		impersonationContext,
	});

	const publishableApiKeys = React.useMemo(() => {
		return stripePublicKeys?.data ? { publishableKey: stripePublicKeys.data } : undefined;
	}, [stripePublicKeys]);

	// Mutation for event registration
	const eventRegistrationMutation = useEventRegistrationMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			toast.error('Failed to create event registration survey');
			logApiError('EventRegistrationSurvey-Error', error);
		},
		onSuccess: async (val: Api.IEventRegistrationSurvey) => {
			toast.success('Event registration survey created');

			history.replace(`/surveys/event/edit/${val.id}`);
			invalidateInfiniteSurveys();
			// Reset form dirty state while keeping current values
			const formValues = getValues();
			reset({ ...formValues }, { keepValues: true, keepDefaultValues: false });
			clearErrors();
		},
	});

	// Mutation for survey updates
	const updateSurveyEventMutation = useUpdateEventSurveyMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			toast.error('Failed to update the event');
			logApiError('EventRegistrationSurvey-Error', error);
		},
		onSuccess: () => {
			toast.success('Updates event success');
			invalidateInfiniteSurveys();
			invalidateSurveyById();
			// Reset form dirty state while keeping current values
			const formValues = getValues();
			reset({ ...formValues }, { keepValues: true, keepDefaultValues: false });
			clearErrors();
		},
	});

	// Mutation for image attachments
	const imageAttachmentMutation = useImageAttachmentMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			toast.error('Failed to upload image');
			logApiError('UploadImageEventRegistrationSurvey-Error', error);
		},
		onSuccess: result => {
			setState(prevState => ({
				...prevState,
				selectedImage: result[0],
			}));
		},
	});

	const reorderSurveyFieldsMutation = useReorderEventRegistrationSurveyFieldsMutation({
		impersonationContext,
		onError: error => {
			errorMessages.pushApiError(error);
			toast.error('Failed to reorder event registration survey fields');
			logApiError('EventRegistrationReorderSurveyFields-Error', error);
		},
		onSuccess: data => {
			toast.success('Event registration survey fields reordered successfully');
			if (onReorderSuccess && data?.fields) {
				onReorderSuccess(data.fields);
			}
		},
	});

	return {
		publishableApiKeys,
		eventRegistrationMutation,
		updateSurveyEventMutation,
		imageAttachmentMutation,
		reorderSurveyFieldsMutation,
	};
};
